/**
 * ZTA access decisions as their own vocabulary (design evaluation D1, Phase 2).
 *
 * src/lib/tone.ts reads "denied" as a Medium severity, "rejected" as a grey
 * closed state and an approved request as resolved, so the same answer was
 * drawn three ways and Deny could be read as a severity of the same rank.
 * The words are normalised here, not in tone.ts, because tone.ts is shared
 * with d2d-XDR and its peers, where those words keep their meanings. Only a
 * call site that renders a decision (OutcomeBadge `decision`) reads this map.
 */
import type { OutcomeTone } from './tone';

export type AccessDecision = 'allow' | 'deny';

const ALIASES: Record<string, AccessDecision> = {
  allow: 'allow', allowed: 'allow', granted: 'allow', approved: 'allow',
  // rejected and denied are one answer (design eval F29).
  deny: 'deny', denied: 'deny', rejected: 'deny', blocked: 'deny',
};

/** The decision a backend word names, or null when it is not a decision. */
export function toAccessDecision(value: string | null | undefined): AccessDecision | null {
  return ALIASES[(value ?? '').trim().toLowerCase()] ?? null;
}

/**
 * Tone for a session status. The shared vocabulary reads "active" as SIEM's
 * newly opened incident (--state-new); a live privileged session is open and
 * healthy, so it takes resolved, as "connected" already does (design eval F29).
 */
export function sessionTone(status: string | null | undefined): OutcomeTone | undefined {
  return (status ?? '').trim().toLowerCase() === 'active' ? 'resolved' : undefined;
}
